/**
 * query_kpi tool — Task C2
 *
 * The advisor's anti-hallucination backbone: REAL numbers come only from here.
 * Reuses the tested walk layer (walkTimeline / walkAbsolute / walkRatio /
 * walkRatioAbsolute) via ApiClient under the user's Auth0 JWT.  No data path
 * bypasses tenant isolation.
 *
 * Design decisions:
 * - `from`/`to` MUST be explicit ISO dates — preset→range resolution needs a
 *   data anchor that is out of scope for this tool (no silent fallback).
 * - Fact table + walk dispatch are resolved from the LIVE chart_registry via
 *   get_charts(page, kpi) — the same seam the dashboard tiles use
 *   (findChartRegistryRow). This is authoritative: the fact is per-(page,kpi)
 *   (ReviewKpis/actual_sales → kpi_overall, not the page's static fact) and the
 *   ratio-vs-standard walk is decided by the registry's walk_function, NOT by
 *   parsing the kpi name. Ratio rows dispatch walk_ratio / walk_ratio_absolute
 *   and carry NO fact_table (the RPC resolves numerator/denominator internally).
 * - No static-fact fallback here: the advisor awaits the registry synchronously,
 *   so the dashboard's load-timing fallback does not apply. A missing row or a
 *   standard row without a fact_table is surfaced LOUDLY as isError.
 * - DimNotApplicableError from the walk layer is surfaced as isError:true with
 *   the offending dim names named — loud, not silent.
 * - All other errors are also surfaced; nothing escapes run().
 */

import type { ApiClient } from '../../api/apiClient'
import { DimNotApplicableError } from '../../api/types'
import { walkTimeline, walkAbsolute, walkRatio, walkRatioAbsolute } from '../../api/endpoints/walks'
import { getCharts } from '../../api/endpoints/getCharts'
import { pickChartRegistryRowForKind } from '../../components/Charts/findChartRegistryRow'
import { clampFromToFloor, getModelFloorIso } from '../../state/modelFloor'
import { PAGE_CATALOG } from '../grounding/pageCatalog'
import type { DatasetOverview } from '../grounding/datasetOverview'
import type { AdvisorTool, ToolResult } from '../runtime/types'

// ---------------------------------------------------------------------------
// Local view type — subset of the ViewSnapshot D4 will provide.
// D4's ViewSnapshot is a superset, so this signature stays compatible.
// ---------------------------------------------------------------------------

interface LocalView {
  pageId: string | null
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

export function makeQueryKpiTool(ctx: {
  apiClient: ApiClient
  getView: () => LocalView
  getOverview: () => DatasetOverview | null
}): AdvisorTool {
  const { apiClient, getView, getOverview } = ctx

  return {
    name: 'query_kpi',

    description: [
      'Fetch REAL KPI figures from the live data warehouse for the current tenant —',
      'from ANY analytics page data context, not only the current one.',
      'This is the sole authoritative source of numbers — NEVER fabricate data;',
      'if this tool returns no rows, say so.',
      'page defaults to the page the user is currently viewing; pass a different pageId',
      '(see the Dataset Overview and page dictionary) to answer questions about data',
      'that lives elsewhere.',
      'Supply concrete ISO-8601 dates (YYYY-MM-DD) in timeframe.from and timeframe.to:',
      'read them from the current-view context (the view snapshot carries the active date range).',
      'Omitting timeframe.from or timeframe.to returns an error —',
      'preset-only date resolution requires a data anchor that is out of scope for this tool.',
      'Pass value_types (raw tokens) to filter multi-value-type KPIs to specific stages.',
    ].join(' '),

    input_schema: {
      type: 'object',
      properties: {
        kpi: {
          type: 'string',
          description:
            'Full encoded KPI string, e.g. "actual_sales", "nsp", "sales_roi__nsp__npv".',
        },
        page: {
          type: 'string',
          enum: Object.keys(PAGE_CATALOG),
          description:
            'Analytics page whose data context to query. Defaults to the page the user is ' +
            'currently viewing. Pass a different pageId to fetch data that lives on another ' +
            'page — the Dataset Overview lists which pages carry which KPIs.',
        },
        timeframe: {
          type: 'object',
          description:
            'Date range. Must supply from+to as YYYY-MM-DD. preset is informational only and is NOT resolved by this tool.',
          properties: {
            preset: { type: 'string', description: 'Preset name (informational only).' },
            from: {
              type: 'string',
              description: 'Start date inclusive (YYYY-MM-DD). Required.',
            },
            to: {
              type: 'string',
              description: 'End date inclusive (YYYY-MM-DD). Required.',
            },
          },
          required: ['from', 'to'],
        },
        filters: {
          type: 'object',
          description:
            'Dimension filters to narrow the result. Keys are dim names, values are arrays of allowed dim values.',
          additionalProperties: { type: 'array', items: { type: 'string' } },
        },
        group_by: {
          type: 'array',
          items: { type: 'string' },
          description: 'Dimension names to group results by.',
        },
        mode: {
          type: 'string',
          enum: ['timeline', 'absolute'],
          description:
            '"timeline" returns per-bucket rows with a time field (default). "absolute" aggregates the whole window into a single row per group.',
        },
        value_types: {
          type: 'array',
          items: { type: 'string' },
          description:
            'Optional value_type row filter (raw tokens, e.g. ["grossadds","netsales"]). Applied to the ' +
            'returned rows exactly like dashboard pin replay: rows without a value_type field always pass. ' +
            'Useful for multi-value-type KPIs such as sales_funnel or actual_sales.',
        },
      },
      required: ['kpi', 'timeframe'],
    },

    async run(input: unknown, signal?: AbortSignal): Promise<ToolResult> {
      try {
        // ------------------------------------------------------------------
        // 1. Narrow input
        // ------------------------------------------------------------------
        if (typeof input !== 'object' || input === null) {
          return { content: 'query_kpi: input must be a JSON object', isError: true }
        }
        const inp = input as Record<string, unknown>

        const kpi = typeof inp['kpi'] === 'string' ? inp['kpi'] : undefined
        if (!kpi) {
          return { content: 'query_kpi: "kpi" is required and must be a string', isError: true }
        }

        const tf =
          typeof inp['timeframe'] === 'object' && inp['timeframe'] !== null
            ? (inp['timeframe'] as Record<string, unknown>)
            : {}

        const from = typeof tf['from'] === 'string' ? tf['from'] : undefined
        const to = typeof tf['to'] === 'string' ? tf['to'] : undefined

        const filters =
          typeof inp['filters'] === 'object' && inp['filters'] !== null
            ? (inp['filters'] as Record<string, string[]>)
            : undefined

        const group_by = Array.isArray(inp['group_by']) ? (inp['group_by'] as string[]) : undefined

        const mode: 'timeline' | 'absolute' = inp['mode'] === 'absolute' ? 'absolute' : 'timeline'

        const value_types = Array.isArray(inp['value_types'])
          ? (inp['value_types'] as unknown[]).filter((v): v is string => typeof v === 'string')
          : undefined

        // ------------------------------------------------------------------
        // 2. Resolve from / to — explicit dates required; no silent fallback
        // ------------------------------------------------------------------
        if (!from || !to) {
          return {
            content:
              'query_kpi requires explicit timeframe.from and timeframe.to (YYYY-MM-DD). ' +
              'Read these from the current-view context and pass them to this tool. ' +
              'Preset-only resolution is not supported here.',
            isError: true,
          }
        }

        // Provenance: the walk endpoints clamp `from` up to the model floor internally.
        // Compute the SAME effective from here (no double-clamp of the walk call — we still
        // forward the requested `from` and the endpoint raises it) so the returned params echo
        // the window the rows actually cover, not the requested one the advisor would misquote.
        const floorIso = getModelFloorIso()
        const effectiveFrom = clampFromToFloor(from, floorIso)
        const clamped = effectiveFrom !== from

        // ------------------------------------------------------------------
        // 3. Resolve the target page: explicit param wins, else current view.
        //    get_charts is keyed by (page, kpi).
        // ------------------------------------------------------------------
        const pageInput = typeof inp['page'] === 'string' ? inp['page'] : undefined
        if (pageInput !== undefined && !(pageInput in PAGE_CATALOG)) {
          return {
            content: `query_kpi: unknown page "${pageInput}". Valid pages: ${Object.keys(PAGE_CATALOG).join(', ')}.`,
            isError: true,
          }
        }
        const pageId = pageInput ?? getView().pageId
        if (!pageId) {
          return {
            content:
              'query_kpi: cannot resolve a page — the user is not on a recognised analytics page. ' +
              `Pass the "page" parameter explicitly (one of: ${Object.keys(PAGE_CATALOG).join(', ')}).`,
            isError: true,
          }
        }

        // ------------------------------------------------------------------
        // 4. Resolve fact table + walk dispatch from the live chart_registry.
        //    mode 'timeline'  → standard walk_timeline / ratio walk_ratio
        //    mode 'absolute'  → standard walk_absolute / ratio walk_ratio_absolute
        //    (getCharts errors — network / zod parse — fall through to the catch.)
        // ------------------------------------------------------------------
        const standardFn = mode === 'absolute' ? 'walk_absolute' : 'walk_timeline'
        const ratioFn = mode === 'absolute' ? 'walk_ratio_absolute' : 'walk_ratio'

        const rows = await getCharts(apiClient, pageId, kpi, signal)
        // Canvas-first, then ANY row: the canvas-tile exclusion list keeps
        // summary/driver-bars/panels rows from shadowing a canvas row's fact,
        // so prefer it — but on CampaignPerformance EVERY enabled row is
        // metric_driver_bars, and a canvas-only lookup resolves nothing there
        // (live F2 finding, 2026-07-03). query_kpi only needs
        // (fact_table, walk variant), so when no canvas row exists the
        // tile-type rows are authoritative.
        let pick = pickChartRegistryRowForKind(rows, standardFn, ratioFn)
        if (pick.status === 'missing') {
          pick = pickChartRegistryRowForKind(rows, standardFn, ratioFn, [])
        }

        if (pick.status === 'missing') {
          const carriers =
            getOverview()
              ?.pages.filter((p) => p.kpis.includes(kpi))
              .map((p) => p.pageId) ?? []
          const hint =
            carriers.length > 0
              ? `This KPI is available on: ${carriers.join(', ')} — pass one as the "page" parameter.`
              : "The KPI may not be available on this page. Call describe_page to see a page's available KPIs."
          return {
            content:
              `query_kpi: no chart_registry row for page "${pageId}" and kpi "${kpi}" ` +
              `(looked for walk functions "${standardFn}"/"${ratioFn}"). ${hint}`,
            isError: true,
          }
        }

        // ------------------------------------------------------------------
        // 5. Dispatch to the walk function the registry row resolves to
        // ------------------------------------------------------------------
        let data: unknown[]
        let rpcName: string
        let fact: string | undefined

        if (pick.variant === 'ratio') {
          // Ratio rows carry NO fact_table — the RPC resolves numerator/denominator internally.
          if (mode === 'absolute') {
            rpcName = 'walk_ratio_absolute'
            data = await walkRatioAbsolute(
              apiClient,
              { kpi, from, to, filter_dims: filters, group_by },
              signal,
            )
          } else {
            rpcName = 'walk_ratio'
            data = await walkRatio(
              apiClient,
              { kpi, from, to, filter_dims: filters, group_by },
              signal,
            )
          }
        } else {
          // Standard row: fact comes from the registry default_params — no static fallback.
          const rawFact = pick.row.default_params['fact_table']
          if (typeof rawFact !== 'string' || rawFact.length === 0) {
            return {
              content:
                `query_kpi: chart_registry row for page "${pageId}" and kpi "${kpi}" ` +
                `(walk function "${pick.row.walk_function}") has no fact_table in default_params. ` +
                'Cannot dispatch a standard walk without it.',
              isError: true,
            }
          }
          fact = rawFact
          if (mode === 'absolute') {
            rpcName = 'walk_absolute'
            data = await walkAbsolute(
              apiClient,
              {
                fact_table: fact,
                kpi,
                from,
                to,
                filter_dims: filters,
                group_by,
                aggregation: 'sum',
              },
              signal,
            )
          } else {
            rpcName = 'walk_timeline'
            data = await walkTimeline(
              apiClient,
              {
                fact_table: fact,
                kpi,
                from,
                to,
                filter_dims: filters,
                group_by,
                aggregation: 'sum',
              },
              signal,
            )
          }
        }

        // ------------------------------------------------------------------
        // 5b. Optional value_types row filter — mirrors dashboard pin replay
        //    exactly (ChartFromPin.tsx:194-198): rows without a value_type
        //    field always pass. Filtering to empty is an honest answer, not
        //    an error — a note names the filter + the value_types actually
        //    present so the model can course-correct.
        // ------------------------------------------------------------------
        let resultRows = data
        let vtNote: string | undefined
        if (value_types && value_types.length > 0) {
          const typed = data as Array<{ value_type?: string | null }>
          resultRows = typed.filter(
            (r) => r.value_type == null || value_types.includes(r.value_type),
          )
          if (resultRows.length === 0 && data.length > 0) {
            const available = [
              ...new Set(typed.map((r) => r.value_type).filter((v): v is string => v != null)),
            ]
            vtNote = `value_types filter [${value_types.join(', ')}] matched no rows; the walk returned value_types [${available.join(', ')}].`
          }
        }

        // ------------------------------------------------------------------
        // 6. Success — return params + rows so the model has full provenance.
        //    `from` is the EFFECTIVE (floor-clamped) window start the rows cover;
        //    when clamped, also surface `requested_from` + an explicit note so the
        //    model never cites a window it did not query (anti-hallucination).
        // ------------------------------------------------------------------
        const notes = [
          clamped
            ? `Requested from ${from} precedes the model floor ${floorIso}; the query window was clamped to start at ${effectiveFrom}.`
            : undefined,
          vtNote,
        ].filter((n): n is string => n != null)

        const params: Record<string, unknown> = {
          rpc: rpcName,
          mode,
          kpi,
          from: effectiveFrom,
          to,
          ...(clamped ? { requested_from: from } : {}),
          ...(notes.length > 0 ? { note: notes.join(' ') } : {}),
          ...(fact ? { fact_table: fact } : {}),
          ...(value_types ? { value_types } : {}),
          filters: filters ?? {},
          group_by: group_by ?? [],
        }
        return { content: JSON.stringify({ params, rows: resultRows }) }
      } catch (err) {
        // ------------------------------------------------------------------
        // 7. Error mapping — nothing escapes run()
        // ------------------------------------------------------------------
        if (err instanceof DimNotApplicableError) {
          return {
            content: `Group-by not applicable: ${err.dims.join(', ')}. Remove or change the group-by.`,
            isError: true,
          }
        }
        return {
          content: err instanceof Error ? err.message : String(err),
          isError: true,
        }
      }
    },
  }
}
